import React, { useEffect, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import useTheme from "../../../hooks/useTheme";
import axios from "axios";
import MainServices from "../../../services/MainServices";
import "./resetPassword.scss";
import { Form, Formik } from "formik";
import { PasswordInput } from "../../../assets/FormElements";
import CustomErrorMessage from "../../../assets/elements/ErrorMessage";
import ButtonLoading from "../../../assets/loaders/ButtonLoading";
import * as Yup from "yup";
import { notifySuccess, notifyWarn } from "../../../assets/elements/Toast";
import { useNavigate } from "react-router-dom";
import * as Storage from "../../../store/LocalStorage";
import TMScontentIcon from "../../../assets/images/loginContent.png";
import LimsDarkThemeLogoWithText from "../../../assets/images/icons/BRAND_LOGOS/LimsDarkThemeLogoWithText.png";

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required("Password is required")
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/,
      "Password must be 8–12 characters with at least one uppercase, lowercase, digit, and special character (@$!%*?&)."
    ),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password"), null], "Passwords must match"),
});

export default function ResetPassword() {
  const { user } = useAuth();
  const { getUserDetails, resetpassword } = MainServices();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const navigate = useNavigate();
  const { theme, themeToggle } = useTheme();

  const nextTheme = theme === "dark" ? "light" : "dark";

  useEffect(() => {
    getUserByUserId();
    // eslint-disable-next-line
  }, []);

  const getUserByUserId = async () => {
    const token = user.token;
    await axios.all([getUserDetails({}, token)]).then(function (res) {
      if (res[0]) {
        let resData = res[0];
        if (resData.success) {
          setUserDetails(resData.data);
        }
      }
    });
  };

  const onPasswordChange = async (values) => {
    setButtonLoading(true);
    const details = { password: values.password };
    await resetpassword(details, userDetails?.publicId, userDetails?.concurrencyStamp)
      .then(async (res) => {
        if (res.success) {
          notifySuccess("Password Changed Successfully");
          Storage.clear();
          window.location.reload();
        } else {
          notifyWarn("Error Occurred!");
        }
      })
      .catch((err) => console.error(err));
    setButtonLoading(false);
  };

  return (
    <div className="reset-password-main">
      {/* ── Theme toggle ── */}
      <div className="reset-theme-toggle">
        <button
          type="button"
          className={`theme-toggle-button theme-toggle-button--${theme}`}
          onClick={themeToggle}
          aria-label={`Switch to ${nextTheme} mode`}
          title={`Switch to ${nextTheme} mode`}
        >
          <span className="theme-toggle-track" aria-hidden="true">
            <span className={`theme-toggle-icon theme-toggle-icon--sun${theme === "light" ? " is-active" : ""}`}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="4.25" stroke="currentColor" strokeWidth="2" />
                <path d="M12 2.75V5.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M12 18.75V21.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M21.25 12H18.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M5.25 12H2.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M18.54 5.46L16.77 7.23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M7.23 16.77L5.46 18.54" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M18.54 18.54L16.77 16.77" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M7.23 7.23L5.46 5.46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <span className="theme-toggle-thumb" />
            <span className={`theme-toggle-icon theme-toggle-icon--moon${theme === "dark" ? " is-active" : ""}`}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20.2 14.2C19.28 17.86 15.98 20.5 12 20.5C7.31 20.5 3.5 16.69 3.5 12C3.5 8.02 6.14 4.72 9.8 3.8C8.96 4.95 8.46 6.36 8.46 7.88C8.46 11.75 11.59 14.88 15.46 14.88C16.98 14.88 18.39 14.38 19.54 13.54C19.8 13.35 20.29 13.84 20.2 14.2Z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
        </button>
      </div>

      {/* ── Left branded panel ── */}
      <div className="reset-left-panel">
        <div className="reset-left-inner">
          <div className="reset-brand">
            <img
              src={LimsDarkThemeLogoWithText}
              alt="LimsPulse"
              className="reset-logo"
            />
            <div className="reset-brand-ring reset-brand-ring--1" />
            <div className="reset-brand-ring reset-brand-ring--2" />
            <div className="reset-brand-ring reset-brand-ring--3" />
          </div>
          <div className="reset-left-content">
            <h1 className="reset-left-title">
              Laboratory Information Management System
            </h1>
            <p className="reset-left-sub">Efficiency · Visibility · Control</p>
          </div>
          <div className="reset-left-illustration">
            <img src={TMScontentIcon} alt="System illustration" />
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="reset-right-panel">
        <div className="reset-form-card">
          <div className="reset-form-head">
            <h2 className="reset-title">Reset Your Password</h2>
            <p className="reset-subtitle">
              Create a new secure password for your account
            </p>
          </div>

          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={resetPasswordSchema}
            onSubmit={(values) => onPasswordChange(values)}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
              <Form className="reset-form" onSubmit={handleSubmit}>
                <div className="reset-field">
                  <label className="reset-label" htmlFor="password">
                    New Password
                  </label>
                  <PasswordInput
                    id="password"
                    placeholder="Enter new password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    name="password"
                  />
                  <CustomErrorMessage name="password" errors={errors} touched={touched} />
                </div>

                <div className="reset-field">
                  <label className="reset-label" htmlFor="confirmPassword">
                    Confirm New Password
                  </label>
                  <PasswordInput
                    id="confirmPassword"
                    placeholder="Confirm new password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.confirmPassword}
                    name="confirmPassword"
                  />
                  <CustomErrorMessage name="confirmPassword" errors={errors} touched={touched} />
                </div>

                <button
                  type="submit"
                  className="reset-submit-btn"
                  disabled={buttonLoading}
                >
                  {buttonLoading ? <ButtonLoading /> : "Change Password"}
                </button>
              </Form>
            )}
          </Formik>
        </div>

        <p className="reset-copyright">
          © 2025 Lims Pulse. All rights reserved.
        </p>
      </div>
    </div>
  );
}
